import React from 'react';

import { Grid, Typography } from '@mui/material';
import usePermissions from 'api/usePermissions';
import { useFormikContext } from 'formik';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import CustomizedSwitch from 'components/SwitchButton/SwitchButton';
import { Flex } from 'components/ui/flex';

import { defaultGeneralTheme } from '../defaults';
import { GeneralThemeFormValues } from '../generalTheme.types';

import 'style/forms.scss';

const externalLinksItems = [
  { content: 'New tab', value: 'New tab' },
  { content: 'Modal', value: 'Modal' }
];

export const FooterSection: React.FC = () => {
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<GeneralThemeFormValues>();
  const { isSuperAdmin } = usePermissions();

  const defaultFooter = values?.footer || defaultGeneralTheme.footer;

  return (
    <AcCard stackContainer={false} title="Footer">
      <Grid container rowSpacing={2} columnSpacing={1.5}>
        <Grid item xs={12}>
          <AcInput
            headerSize="14"
            header="Appstore Link"
            name="footer.appstoreLink"
            value={defaultFooter.appstoreLink}
            placeholder="https://www.apple.com/app-store/"
            dataTestId="footerAppstoreLink"
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.footer?.appstoreLink &&
              Boolean(errors.footer?.appstoreLink)
            }
            helperText={
              touched.footer?.appstoreLink
                ? errors.footer?.appstoreLink?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            headerSize="14"
            header="GooglePlay Link"
            name="footer.googlePlayLink"
            value={defaultFooter.googlePlayLink}
            placeholder="https://play.google.com/"
            dataTestId="footerGooglePlayLink"
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.footer?.googlePlayLink &&
              Boolean(errors.footer?.googlePlayLink)
            }
            helperText={
              touched.footer?.googlePlayLink
                ? errors.footer?.googlePlayLink?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            required
            headerSize="14"
            header="Privacy Policy"
            name="footer.privacyPolicyUrl"
            value={defaultFooter.privacyPolicyUrl}
            dataTestId="footerPrivacyPolicyUrl"
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.footer?.privacyPolicyUrl &&
              Boolean(errors.footer?.privacyPolicyUrl)
            }
            helperText={
              touched.footer?.privacyPolicyUrl
                ? errors.footer?.privacyPolicyUrl?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            required
            headerSize="14"
            header="Terms and Conditions"
            name="footer.termsAndConditionsUrl"
            value={defaultFooter.termsAndConditionsUrl}
            dataTestId="footerTermsAndConditionsUrl"
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.footer?.termsAndConditionsUrl &&
              Boolean(errors.footer?.termsAndConditionsUrl)
            }
            helperText={
              touched.footer?.termsAndConditionsUrl
                ? errors.footer?.termsAndConditionsUrl?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            headerSize="14"
            header="Support Link"
            name="footer.supportLink"
            value={defaultFooter.supportLink}
            dataTestId="footerSupportLink"
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.footer?.supportLink && Boolean(errors.footer?.supportLink)
            }
            helperText={
              touched.footer?.supportLink
                ? errors.footer?.supportLink?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            headerSize="14"
            header="Player ID Parameter Name"
            name="footer.supportParams.playerIdParamName"
            value={defaultFooter.supportParams?.playerIdParamName}
            placeholder="e.g., playerId, uid, customer_id"
            dataTestId="footerPlayerIdParamName"
            onChange={handleChange}
            onBlur={handleBlur}
            helperText="Leave empty to not include player ID in support URL"
          />
        </Grid>
        <Grid item xs={12}>
          <AcInput
            headerSize="14"
            header="Player Name Parameter Name"
            name="footer.supportParams.playerNameParamName"
            value={defaultFooter.supportParams?.playerNameParamName}
            placeholder="e.g., name, user_name, customer_name"
            dataTestId="footerPlayerNameParamName"
            onChange={handleChange}
            onBlur={handleBlur}
            helperText="Leave empty to not include player name in support URL"
          />
        </Grid>
        {isSuperAdmin && (
          <Grid item xs={12}>
            <AcSelect
              header="External links"
              name="footer.openLinksInNewTab"
              value={defaultFooter.openLinksInNewTab ? 'New tab' : 'Modal'}
              dataTestBoxId="footerOpenLinksInNewTab"
              dataTestListId="footerOpenLinksInNewTabList"
              optionDataTestIdPrefix="footerOpenLinksInNewTabOption"
              items={externalLinksItems}
              onChange={(e) => {
                setFieldValue(
                  'footer.openLinksInNewTab',
                  Boolean(e.target.value === 'New tab')
                );
              }}
              onBlur={handleBlur}
              error={
                touched.footer?.openLinksInNewTab &&
                Boolean(errors.footer?.openLinksInNewTab)
              }
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <AcCard className="mt-0">
            <Flex direction="row" align="center" className="mb-4">
              <Typography variant="body2" marginRight={'16px'}>
                Show "Cookie settings" link
              </Typography>
              <CustomizedSwitch
                status={!!defaultFooter.showCookieSettings}
                texts={[]}
                functions={[
                  () => setFieldValue('footer.showCookieSettings', false),
                  () => setFieldValue('footer.showCookieSettings', true)
                ]}
                dataTestId="footerShowCookieSettings"
              />
            </Flex>
          </AcCard>
        </Grid>
      </Grid>
    </AcCard>
  );
};
